import { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Table,
  TableBody,
  TableRow,
  TableCell,
  TableHead,
  Box,
  Typography,
  Avatar,
} from "@mui/material";
import { toast } from "react-toastify";
import {
  fetchSalesReturn,
  createCreditNote,
} from "../../app/services/OrdersService";
import PageWrapper from "../../app/shared/common/PageWrapper";
import CreateTable from "../../app/shared/common/table/CreateTable";
import ImagePreview from "./imagePreview";

const tableColumns = [
  { key: "salesReturnId", label: "Sales Return ID" },
  { key: "orderId", label: "Order ID" },
  { key: "totalAmount", label: "Order Amount" },
  { key: "returnAmount", label: "Return Amount" },
  { key: "totalItems", label: "Returned Items" },
  { key: "createdAt", label: "Created At" },
  { key: "actions", label: "Actions" }, 
];

const SalesReturnList = () => {
  const [salesReturns, setSalesReturns] = useState<any[]>([]);
  const [loading, setLoading] = useState<boolean>(true);

  // popup state
  const [selectedItems, setSelectedItems] = useState<any[]>([]);
  const [selectedImages, setSelectedImages] = useState<any[]>([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [notes, setNotes] = useState(false);

  const loadSalesReturns = async () => {
    try {
      setLoading(true);
      const res = await fetchSalesReturn({});
      if (res.status === "success") {
        const mappedData = res.data.map((item: any) => {
        const returnAmount =
          item.items?.reduce((sum: number, it: any) => {
            const qty = Number(it.quantity) || 0;
            const basePrice = Number(it.product?.base_price) || 0;
            return sum + qty * basePrice;
          }, 0) || 0;

        const totalQuantity = item.items?.reduce(
          (sum: number, it: any) => sum + (Number(it.quantity) || 0),
          0
        );

        return {
          id: item.id,
          salesReturnId: item.id,
          orderId: item.order?.unique_id ?? "-",
          totalAmount: item.order
            ? `₹${Number(item.order?.total_amount || 0).toFixed(2)}`
            : "-",
          returnAmount: `₹${returnAmount.toFixed(2)}`,
          totalItems: {
            count: totalQuantity,
            items: item.items ?? [],
            images: [],
            notes: item.notes,
          },
          createdAt: new Date(item.created_at).toLocaleString(),
        };
      });
        setSalesReturns(mappedData);
      } else {
        toast.error(res.message);
      }
    } catch (err) {
  
      toast.error("Failed to load sales returns.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadSalesReturns();
  }, []);

  const handleOpenItems = (value: any) => {
    setSelectedItems(value.items);
    setNotes(value.notes);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedItems([]);
    setSelectedImages([]);
  };

  const popupTotal = selectedItems.reduce((sum, it) => {
    const qty = Number(it.quantity) || 0;
    const basePrice = Number(it.product?.base_price) || 0;
    return sum + qty * basePrice;
  }, 0);

  const handleCreateCreditNote = async (salesReturnId: number) => {
    const payload = { sales_return_id: salesReturnId };

    try {
      const res = await createCreditNote(payload);
      if (res.data?.status === "success") {
        toast.success(res.data.message || "Credit note created successfully");
        loadSalesReturns();
      } else {
        toast.error(res.data?.message || "Failed to create credit note.");
      }
    } catch (error) {
      toast.error("Error creating credit note.");
    }
  };

  return (
    <PageWrapper title={"Sales Return"}>
      <CreateTable
        data={salesReturns}
        columns={tableColumns}
        loading={loading}
        customRender={{
          totalItems: (value: any) => (
            <Button
              variant="text"
              size="small"
              onClick={() => handleOpenItems(value)}
            >
              {value.count}
            </Button>
          ),
          actions: (value: any, row: any) => (
            <Button
              variant="contained"
              color="primary"
              size="small"
              onClick={() => handleCreateCreditNote(row.salesReturnId)}
            >
              Credit Note
            </Button>
          ),
        }}
      />

      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>Returned Items</DialogTitle>
        <DialogContent>
          {selectedImages?.length > 0 && (
            <Box display="flex" gap={2} flexWrap="wrap" mb={2}>
              {selectedImages.slice(0, 6).map((img, idx) => (
                <img
                  key={idx}
                  src={img.image}
                  alt={`returned-${idx}`}
                  width="60"
                  height="60"
                  style={{
                    borderRadius: "8px",
                    objectFit: "cover",
                    border: "1px solid #ddd",
                  }}
                />
              ))}
            </Box>
          )}

          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Product</TableCell>
                <TableCell>SKU</TableCell>
                <TableCell>Base Price</TableCell>
                <TableCell>Returned By</TableCell>
                <TableCell>Reason</TableCell>
                <TableCell>Quantity</TableCell>
                <TableCell>Amount</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {selectedItems.map((it, index) => {
                const qty = Number(it.quantity) || 0;
                const basePrice = Number(it.product?.base_price) || 0;
                const amount = qty * basePrice;
                const isFirstOccurrence =
                  selectedItems.findIndex(
                    (p) => p.product?.id === it.product?.id
                  ) === index;

                return (
                  <TableRow key={it.id}>
                    {isFirstOccurrence ? (
                      <>
                        <TableCell>
                          <div style={{ display: "flex" }}>
                            <span>
                              {it.images && it.images.length > 0 ? (
                                <ImagePreview images={it.images} />
                              ) : null}
                            </span>
                            <span style={{ paddingTop: 7, paddingLeft: 5 }}>
                              {it.product?.name}
                            </span>
                          </div>
                        </TableCell>
                        <TableCell>{it.product?.sku}</TableCell>
                        <TableCell>₹{basePrice.toFixed(2)}</TableCell>
                        <TableCell></TableCell>
                        <TableCell>{it.reason?.name}</TableCell>
                        <TableCell>{qty}</TableCell>
                        <TableCell>₹{amount.toFixed(2)}</TableCell>
                      </>
                    ) : (
                      <>
                        <TableCell colSpan={3}></TableCell>
                        <TableCell></TableCell>
                        <TableCell>{it.reason?.name}</TableCell>
                        <TableCell>{qty}</TableCell>
                        <TableCell>₹{amount.toFixed(2)}</TableCell>
                      </>
                    )}
                  </TableRow>
                );
              })}

              <TableRow>
                <TableCell align="left">
                  <Typography fontWeight={600}>Note:{notes}</Typography>
                </TableCell>
                <TableCell colSpan={5} align="right">
                  <Typography fontWeight={600}>Total Return Amount</Typography>
                </TableCell>
                <TableCell>
                  <Typography fontWeight={600}>₹{popupTotal.toFixed(2)}</Typography>
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Close</Button>
        </DialogActions>
      </Dialog>
    </PageWrapper>
  );
};

export default SalesReturnList;
